import React, { useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  FileSpreadsheet,
  Info,
  Loader2,
} from 'lucide-react';
import { missingMapping, recordColumnSample, recordFields } from './recordImport';

// What each import links to, shown so the order of imports makes sense.
const LINK_NOTES = {
  projects:
    "Projects link to the studio's clients by name and email, and to its sales tax rates by name, so import Clients and Sales Tax Rates first. A client that isn't there yet is added from the project's contact details.",
  rooms: "Rooms go on the studio's projects, matched by project name, so import Projects first.",
  proposals:
    "Proposals go on the studio's projects, matched by project name, so import Projects first. They come in Archived: kept for reference, hidden from the client and owing nothing. Choose Draft in the review for any the studio is still working on.",
  timeEntries:
    "Time goes on the studio's projects, matched by project name, so import Projects first.",
  vendors:
    'Vendors are matched to the studio’s existing vendors by name only. Showrooms often share one email across many vendors, so email isn’t used.',
};

const count = (n) => n.toLocaleString('en-US');
const plural = (n, one, many) => `${count(n)} ${n === 1 ? one : many}`;

// What an Ivy export holds for the proposal import, and what it leaves out.
function IvyProposalSummary({ read }) {
  const { stats } = read;
  const left = [
    ...Object.entries(stats.otherDocuments).map(([kind, n]) =>
      plural(n, kind.toLowerCase(), `${kind.toLowerCase()}s`)
    ),
    stats.removedProposals > 0 &&
      plural(stats.removedProposals, 'removed proposal', 'removed proposals'),
    stats.removedLines > 0 && plural(stats.removedLines, 'removed line', 'removed lines'),
    stats.emptyProposals > 0 &&
      plural(stats.emptyProposals, 'proposal with no lines', 'proposals with no lines'),
  ].filter(Boolean);
  return (
    <div className="pi-proposal-source">
      <p>
        <strong>{plural(stats.proposals, 'proposal', 'proposals')}</strong> with{' '}
        {plural(stats.lines, 'line', 'lines')}, read from Ivy&apos;s Invoices and Invoice Items
        sections. Proposal numbers stay as they were in Ivy.
      </p>
      {left.length > 0 && <p className="pi-muted">Left out: {left.join(', ')}.</p>}
      <p className="pi-muted">
        Ivy&apos;s export doesn&apos;t say which lines were taxed, so it&apos;s worked out from each
        proposal&apos;s tax. The review flags any proposal whose total still differs from
        Ivy&apos;s.
      </p>
    </div>
  );
}

// How a sheet of proposal lines is read.
function ProposalOptions({ mapping, options, onChange }) {
  const askSellMode = mapping.includes('unitPrice') && mapping.includes('markup');
  return (
    <div className="pi-options-row pi-proposal-options">
      {askSellMode && (
        <label className="pi-field">
          <span className="pi-field-label">Unit Selling Price</span>
          <select
            id="pi-sell-mode"
            value={options.sellMode}
            onChange={(e) => onChange({ sellMode: e.target.value })}
          >
            <option value="withMarkup">
              Is before markup; add the Markup % (Houzz Pro, Ivy trackers)
            </option>
            <option value="file">Is what the client pays</option>
          </select>
        </label>
      )}
      <label className="pi-field">
        <span className="pi-field-label">Lines without a proposal number go on</span>
        <select
          id="pi-group-by"
          value={options.groupBy}
          onChange={(e) => onChange({ groupBy: e.target.value })}
        >
          <option value="project">One proposal per project</option>
          <option value="room">One proposal per room</option>
        </select>
      </label>
    </div>
  );
}

export default function RecordMapStep({
  type,
  fileName,
  sheet,
  mapping,
  onChangeField,
  ivyProposals = null,
  proposalOptions,
  onChangeProposalOptions,
  contextLoading,
  contextError,
  onRetryContext,
  onBack,
  onContinue,
}) {
  const fields = recordFields(type);
  // Worked out once when the step opens so rows don't jump while fields change:
  // matched columns in field order, then the rest as they sit in the file.
  const [columnOrder] = useState(() => {
    const order = fields.map((f) => f.key);
    const columns = mapping.map((field, col) => ({ field, col }));
    const matched = columns
      .filter((c) => c.field)
      .sort((a, b) => order.indexOf(a.field) - order.indexOf(b.field));
    return [...matched, ...columns.filter((c) => !c.field)].map((c) => c.col);
  });
  let missing = missingMapping(type, mapping);
  if (ivyProposals) missing = ivyProposals.entries.length ? '' : 'This file has no proposals.';

  return (
    <>
      <div className="pi-card pi-file-chip">
        <FileSpreadsheet className="pi-excel-icon" size={22} aria-hidden="true" />
        <span>{fileName}</span>
        {sheet.section && !ivyProposals && (
          <span className="pi-chip pi-chip--info">{sheet.name}</span>
        )}
      </div>

      <section className="pi-card pi-map-card">
        {ivyProposals ? (
          <IvyProposalSummary read={ivyProposals} />
        ) : (
          <div className="pi-table-wrap">
            <table className="pi-table pi-map-table">
              <thead>
                <tr>
                  <th>Excel column</th>
                  <th>Sample from Excel</th>
                  <th>InteriorFolio field</th>
                </tr>
              </thead>
              <tbody>
                {columnOrder.map((col) => {
                  const header = sheet.headers[col];
                  const field = mapping[col];
                  const sample = recordColumnSample(type, sheet.dataRows, col, field, sheet);
                  return (
                    <tr key={`${header}-${col}`}>
                      <td className="pi-map-header">{header}</td>
                      <td className={sample ? 'pi-map-sample' : 'pi-map-sample is-empty'}>
                        {sample || 'Empty'}
                      </td>
                      <td>
                        <select
                          id={`pi-map-${col}`}
                          className={field ? 'is-mapped' : ''}
                          value={field}
                          onChange={(e) => onChangeField(col, e.target.value)}
                          aria-label={`InteriorFolio field for ${header}`}
                        >
                          <option value="">Don't import</option>
                          {fields.map((f) => (
                            <option key={f.key} value={f.key}>
                              {f.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {type === 'proposals' && !ivyProposals && (
          <ProposalOptions
            mapping={mapping}
            options={proposalOptions}
            onChange={onChangeProposalOptions}
          />
        )}
        {missing && (
          <div className="pi-banner pi-banner--error" role="alert">
            <AlertTriangle size={18} aria-hidden="true" />
            {missing}
          </div>
        )}
        {LINK_NOTES[type] && (
          <div className="pi-banner pi-banner--info">
            <Info size={18} aria-hidden="true" />
            {LINK_NOTES[type]}
          </div>
        )}
        {contextError && (
          <div className="pi-banner pi-banner--error" role="alert">
            <AlertCircle size={18} aria-hidden="true" />
            <span>{contextError}</span>
            <div className="pi-banner-actions">
              <button type="button" className="pi-btn pi-btn--outline" onClick={onRetryContext}>
                Try again
              </button>
            </div>
          </div>
        )}
      </section>

      <div className="pi-footer">
        <button type="button" className="pi-btn pi-btn--outline" onClick={onBack}>
          <ArrowLeft size={16} aria-hidden="true" /> Back to Upload
        </button>
        <button
          type="button"
          className="pi-btn pi-btn--primary pi-btn--lg"
          onClick={onContinue}
          disabled={Boolean(missing) || contextLoading || Boolean(contextError)}
        >
          {contextLoading ? (
            <>
              <Loader2 size={16} className="pi-spin" aria-hidden="true" /> Loading the studio&apos;s
              records…
            </>
          ) : (
            <>
              Continue to Review <ArrowRight size={16} aria-hidden="true" />
            </>
          )}
        </button>
      </div>
    </>
  );
}
